// monitoring-ui/components/MonitoringDashboard.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMonitoring }  from '../Hooks/useMonitoring';
import { OverviewTab }    from '../Components/Overviewtab';
import { DeploymentsTab } from '../Components/Deploymentstab';
import { CostsTab }       from '../Components/Coststab';
import { AIActivityTab }  from '../Components/AiActivitytab';
import { AlertsTab }      from '../Components/Alertstab';
import { Spinner }        from '../Components/MonitoringUi';

// ─── Types ────────────────────────────────────────────────────────────────────
type Tab = 'overview' | 'deployments' | 'costs' | 'ai' | 'alerts';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview',    label: 'Overview'    },
  { id: 'deployments', label: 'Deployments' },
  { id: 'costs',       label: 'Costs'       },
  { id: 'ai',          label: 'AI Activity' },
  { id: 'alerts',      label: 'Alerts'      },
];

// ─── Dummy data shown behind the upgrade gate ─────────────────────────────────
const DUMMY_DEPLOYMENTS = [
  { app: 'api-prod-1',    cpu: 72, mem: 81, status: 'Critical', cost: '$142.30', alerts: 2, dot: '#f43f5e' },
  { app: 'web-frontend',  cpu: 34, mem: 48, status: 'Healthy',  cost: '$58.20',  alerts: 0, dot: '#22c55e' },
  { app: 'worker-queue',  cpu: 91, mem: 67, status: 'Warning',  cost: '$89.10',  alerts: 1, dot: '#eab308' },
  { app: 'auth-service',  cpu: 22, mem: 31, status: 'Healthy',  cost: '$41.75',  alerts: 0, dot: '#22c55e' },
  { app: 'ml-inference',  cpu: 88, mem: 94, status: 'Critical', cost: '$318.60', alerts: 3, dot: '#f43f5e' },
];

const DUMMY_ALERTS = [
  { severity: 'critical', app: 'ml-inference',  metric: 'Memory at 94%',    ago: '3 min ago',  dot: '#f43f5e' },
  { severity: 'critical', app: 'api-prod-1',    metric: 'CPU spike at 72%', ago: '11 min ago', dot: '#f43f5e' },
  { severity: 'warning',  app: 'worker-queue',  metric: 'Queue depth 8.4k', ago: '28 min ago', dot: '#eab308' },
];

const DUMMY_COSTS = [
  { label: 'EC2',           value: '$421', bar: 58, color: '#6366f1' },
  { label: 'ECS',           value: '$184', bar: 25, color: '#818cf8' },
  { label: 'RDS',           value: '$97',  bar: 13, color: '#a5b4fc' },
  { label: 'Data transfer', value: '$29',  bar: 4,  color: '#c7d2fe' },
];

// ─── Upgrade Gate ─────────────────────────────────────────────────────────────
const UpgradeGate: React.FC<{ planName: string | undefined }> = ({ planName }) => {
  const navigate = useNavigate();

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#09090e', fontFamily: "'Inter',sans-serif", overflow: 'hidden' }}>

      {/* Blurred dummy content */}
      <div style={{ filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.55 }}>
        <div style={{ height: '46px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: '#eeeef5' }}>Infrastructure Monitor</span>
          <span style={{ fontSize: '11px', color: '#52525f', marginLeft: '8px' }}>Last sync 14:32:01</span>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
            {TABS.map(tab => (
              <div key={tab.id} style={{ padding: '5px 11px', borderRadius: '6px', fontSize: '11.5px', background: tab.id === 'overview' ? '#6366f1' : 'transparent', color: tab.id === 'overview' ? '#fff' : '#8e8ea8' }}>
                {tab.label}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
            {[
              { label: 'Deployments',   value: '5',     sub: '3 healthy · 1 warn · 2 crit', color: '#eeeef5' },
              { label: 'Avg uptime',    value: '99.7%', sub: '↑ 0.2% vs last week',         color: '#22c55e' },
              { label: 'MTD cost',      value: '$731',  sub: 'Projected $890 EOM',           color: '#eeeef5' },
              { label: 'Active alerts', value: '5',     sub: '2 critical · 3 warning',       color: '#f43f5e' },
            ].map(c => (
              <div key={c.label} style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '6px' }}>{c.label}</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: c.color, fontFamily: "'Syne',sans-serif", marginBottom: '4px' }}>{c.value}</div>
                <div style={{ fontSize: '11px', color: '#52525f' }}>{c.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #26262f', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f' }}>
                Live deployment health
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '11.5px' }}>
                <thead>
                  <tr style={{ background: '#0d0d12' }}>
                    {['App', 'CPU', 'Mem', 'Status', 'Cost/mo'].map(h => (
                      <th key={h} style={{ padding: '7px 12px', textAlign: 'left' as const, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.05em', color: '#52525f', borderBottom: '1px solid #26262f' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DUMMY_DEPLOYMENTS.map((d, i) => (
                    <tr key={d.app} style={{ borderBottom: i < DUMMY_DEPLOYMENTS.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: '#eeeef5' }}>{d.app}</td>
                      <td style={{ padding: '8px 12px', color: d.cpu > 80 ? '#f43f5e' : '#eeeef5' }}>{d.cpu}%</td>
                      <td style={{ padding: '8px 12px', color: d.mem > 85 ? '#f43f5e' : '#eeeef5' }}>{d.mem}%</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 7px', borderRadius: '20px', fontSize: '10px', fontWeight: 600, background: d.dot === '#22c55e' ? 'rgba(34,197,94,.1)' : d.dot === '#eab308' ? 'rgba(234,179,8,.1)' : 'rgba(244,63,94,.1)', color: d.dot, border: `1px solid ${d.dot}33` }}>
                          <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: d.dot }} />{d.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: '#eeeef5' }}>{d.cost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ background: '#111116', border: '1px solid rgba(244,63,94,.2)', borderRadius: '10px', padding: '12px 14px', flex: '1' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>Active alerts</div>
                {DUMMY_ALERTS.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: i > 0 ? '7px 0 0' : '0', borderTop: i > 0 ? '1px solid #1a1a22' : 'none', marginTop: i > 0 ? '7px' : 0 }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: a.dot, flexShrink: 0, marginTop: '3px' }} />
                    <div>
                      <div style={{ fontSize: '11.5px', fontWeight: 500, color: a.dot }}>{a.app}</div>
                      <div style={{ fontSize: '10.5px', color: '#52525f' }}>{a.metric} · {a.ago}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', padding: '12px 14px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '10px' }}>MTD spend by service</div>
                {DUMMY_COSTS.map(c => (
                  <div key={c.label} style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#8e8ea8', marginBottom: '3px' }}>
                      <span>{c.label}</span><span style={{ color: '#eeeef5', fontWeight: 600 }}>{c.value}</span>
                    </div>
                    <div style={{ height: '4px', background: '#1e1e27', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${c.bar}%`, height: '100%', background: c.color, borderRadius: '2px' }} />
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, color: '#eeeef5', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #26262f' }}>
                  <span>Total MTD</span><span>$731</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade overlay */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          background: 'rgba(9,9,14,0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(99,102,241,.25)', borderRadius: '20px', padding: '40px 48px',
          maxWidth: '480px', width: '90%', display: 'flex', flexDirection: 'column',
          alignItems: 'center', textAlign: 'center' as const, gap: '0',
          boxShadow: '0 32px 80px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.04)',
        }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '14px', marginBottom: '18px', background: 'linear-gradient(135deg, rgba(99,102,241,.2), rgba(168,85,247,.15))', border: '1px solid rgba(99,102,241,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="1.5">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
            </svg>
          </div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '10px', lineHeight: 1.3 }}>
            Infrastructure Monitor
          </div>
          <div style={{ fontSize: '13px', color: '#8e8ea8', lineHeight: 1.7, marginBottom: '28px', maxWidth: '340px' }}>
            Get live CPU, memory, and cost data across all your deployments — with instant alerts and AI-driven rightsizing suggestions.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '8px', justifyContent: 'center', marginBottom: '28px' }}>
            {[
              { icon: '⚡', label: 'Live metrics via WebSocket' },
              { icon: '🔔', label: 'Threshold alerts'          },
              { icon: '💰', label: 'Cost breakdowns'           },
              { icon: '🤖', label: 'AI rightsizing'            },
            ].map(f => (
              <div key={f.label} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '20px', fontSize: '11.5px', background: 'rgba(99,102,241,.08)', color: '#c7d2fe', border: '1px solid rgba(99,102,241,.15)' }}>
                <span>{f.icon}</span>{f.label}
              </div>
            ))}
          </div>
          {planName && (
            <div style={{ fontSize: '11.5px', color: '#52525f', marginBottom: '18px' }}>
              Your current plan —{' '}
              <span style={{ fontWeight: 600, color: '#8e8ea8', textTransform: 'capitalize' as const }}>{planName}</span>
              {' '}— does not include this feature.
            </div>
          )}
          <button
            onClick={() => navigate('/plans')}
            style={{ width: '100%', padding: '11px 0', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #6366f1, #818cf8)', color: '#fff', boxShadow: '0 4px 20px rgba(99,102,241,.35)' }}
          >
            Upgrade to unlock →
          </button>
          <div style={{ fontSize: '11px', color: '#52525f', marginTop: '12px' }}>Available on Starter and above</div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export const MonitoringDashboard: React.FC = () => {
  const {
    deployments, costSummary, deploymentCosts, rightsizing,
    activeAlerts, isLoading, error, lastUpdated, wsConnected,
    activeTab, setActiveTab, resolveAlert, loadAll,
    hasMonitorAccess, currentPlan,
  } = useMonitoring();

  const totalAlerts = (activeAlerts ?? []).length;

  if (isLoading) {
    return (
      <div style={{ background: '#09090e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }}/>
        <span style={{ fontSize: '13px', color: '#52525f' }}>Loading…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!hasMonitorAccess) {
    return <UpgradeGate planName={currentPlan?.plan_name} />;
  }

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Topbar */}
      <div style={{ height: '46px', background: '#111116', borderBottom: '1px solid #26262f', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '10px', position: 'sticky' as const, top: 0, zIndex: 50 }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#6366f1" strokeWidth="1.5" aria-hidden="true">
          <rect x="1" y="1" width="6" height="6" rx="1.5"/><rect x="9" y="1" width="6" height="6" rx="1.5"/>
          <rect x="1" y="9" width="6" height="6" rx="1.5"/><rect x="9" y="9" width="6" height="6" rx="1.5"/>
        </svg>
        <span style={{ fontSize: '13px', fontWeight: 500, color: 'white' }}>Infrastructure Monitor</span>
        <span style={{ color: '#26262f' }}>·</span>
        <span style={{ fontSize: '11px', color: '#52525f' }}>
          {lastUpdated
            ? `Last sync ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
            : 'Loading…'}
        </span>
        <span
          title={wsConnected ? 'Live' : 'Reconnecting…'}
          style={{ width: '6px', height: '6px', borderRadius: '50%', background: wsConnected ? '#22c55e' : '#f43f5e', display: 'inline-block', marginLeft: '2px' }}
        />

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{ padding: '5px 11px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 500, cursor: 'pointer', border: 'none', transition: 'all .15s', background: activeTab === tab.id ? '#6366f1' : 'transparent', color: activeTab === tab.id ? '#fff' : '#8e8ea8' }}
            >
              {tab.label}
              {tab.id === 'alerts' && totalAlerts > 0 && (
                <span style={{ background: 'rgba(244,63,94,.2)', color: '#f43f5e', borderRadius: '10px', padding: '0 5px', fontSize: '10px', marginLeft: '4px' }}>
                  {totalAlerts}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 20px' }}>

        {error && (
          <div style={{ background: 'rgba(244,63,94,.08)', border: '1px solid rgba(244,63,94,.2)', borderRadius: '8px', padding: '10px 14px', fontSize: '12.5px', color: '#f43f5e', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {error}
            <button onClick={loadAll} disabled={isLoading} style={{ background: 'transparent', border: '1px solid rgba(244,63,94,.3)', color: '#f43f5e', borderRadius: '5px', padding: '3px 9px', fontSize: '11px', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.6 : 1 }}>
              {isLoading ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {activeTab === 'overview' && (
          <OverviewTab
            deployments={deployments ?? []}
            costSummary={costSummary}
            activeAlerts={activeAlerts ?? []}
            onTabChange={setActiveTab}
          />
        )}
        {activeTab === 'deployments' && (
          <DeploymentsTab
            deployments={deployments ?? []}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'costs' && (
          <CostsTab
            costSummary={costSummary}
            deploymentCosts={deploymentCosts ?? []}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'ai' && (
          <AIActivityTab />
        )}
        {activeTab === 'alerts' && (
          <AlertsTab
            activeAlerts={activeAlerts ?? []}
            deployments={deployments ?? []}
            onResolve={resolveAlert}
          />
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};